import { Link, useParams } from "react-router-dom";
import { Newspaper, RefreshCw } from "lucide-react";
import { ArticleCard } from "@/components/public/ArticleCard";
import { useI18n } from "@/lib/i18n";
import { useCategories } from "@/hooks/useCategories";
import { useArticles } from "@/hooks/useArticles";
import { adaptArticles } from "@/lib/adapters";
import { useSeo, useJsonLd } from "@/hooks/useSeo";
import { SITE_URL, truncate } from "@/lib/seo";
import { estCategorieEducation } from "@/lib/categories";
import type { CategorieDto } from "@/types/categorie.types";

function ArticleCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="aspect-[16/9] rounded-md bg-muted" />
      <div className="h-3 w-16 rounded bg-muted" />
      <div className="space-y-1.5">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 w-3/4 rounded bg-muted" />
      </div>
      <div className="h-3 w-1/2 rounded bg-muted" />
    </div>
  );
}

function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { locale, t } = useI18n();

  const {
    data: categoriesData,
    isLoading: loadingCats,
  } = useCategories();

  const {
    data: articlesData,
    isLoading: loadingArticles,
    isError: errorArticles,
    refetch: refetchArticles,
  } = useArticles(0, 100, "PUBLIE");

  const categories: CategorieDto[] = Array.isArray(categoriesData) ? categoriesData : [];
  const cat = categories.find((c) => c.slug === slug);

  // Filter articles by current category id
  const rawDtos = Array.isArray(articlesData) ? articlesData : (articlesData as any)?.content ?? [];
  const filteredDtos = cat ? rawDtos.filter((a: any) => a.categorieIds?.includes(cat.id)) : [];
  const items = adaptArticles(filteredDtos);

  const isLoading = loadingCats || loadingArticles;

  const catLabel = cat ? (locale === "fr" ? cat.libelleFr : cat.libelleEn) : slug ?? "";
  // "Éducation aux médias" est une catégorie à part : elle ne doit pas proposer
  // de bascule vers les autres catégories, seulement ses propres articles.
  const estEducation = cat ? estCategorieEducation(cat) : false;

  useSeo({
    title: `${catLabel} — Ramba News`,
    description: truncate(
      locale === "fr"
        ? `Retrouvez tous les articles de la catégorie ${catLabel} sur Ramba News.`
        : `All articles in the ${catLabel} category on Ramba News.`
    ),
    path: `/categories/${slug}`,
  });

  useJsonLd("ld-breadcrumb", cat ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: locale === "fr" ? "Accueil" : "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: catLabel, item: `${SITE_URL}/categories/${slug}` },
    ],
  } : undefined);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 md:py-14">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-verified">{t("category.eyebrow")}</div>
      <h1 className="mt-2 font-serif text-3xl md:text-5xl font-semibold tracking-tight">
        {loadingCats ? (
          <span className="inline-block h-9 w-48 rounded bg-muted animate-pulse" />
        ) : cat ? (
          locale === "fr" ? cat.libelleFr : cat.libelleEn
        ) : (
          slug
        )}
      </h1>

      {/* Filtre par catégorie — masqué pour la categorie"Éducation aux médias", qui ne
          doit afficher que ses propres articles, sans bascule vers les autres */}
      {!estEducation && (
        <div className="mt-6 flex flex-wrap gap-2 border-b border-border pb-3">
          {loadingCats ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-7 w-20 rounded-md bg-muted animate-pulse" />
            ))
          ) : (
            categories.map((c) => (
              <Link
                key={c.slug}
                to={`/categories/${c.slug}`}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  c.slug === slug
                    ? "bg-foreground text-background"
                    : "bg-surface text-muted-foreground hover:text-foreground"
                }`}
              >
                {locale === "fr" ? c.libelleFr : c.libelleEn}
              </Link>
            ))
          )}
        </div>
      )}

      {/* Liste des articles */}
      <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <ArticleCardSkeleton key={i} />)
        ) : errorArticles ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12 gap-3">
            <p className="text-sm text-muted-foreground">{t("error.load")}</p>
            <button
              onClick={() => refetchArticles()}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold hover:bg-surface transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" /> {t("cta.retry")}
            </button>
          </div>
        ) : items.length > 0 ? (
          items.map((a) => <ArticleCard key={a.id} article={a} />)
        ) : (
          <div className="col-span-full flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
            <Newspaper className="h-8 w-8 opacity-25" />
            <p className="text-sm">{t("category.empty")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoryPage;
